import { CodeCopy, Col, H2, Image, PortableText, Row, Section } from '@ahmdigital/ui';
import { isEmpty, last } from 'lodash/fp';
import { useSelector } from 'react-redux';
import generatePortableText from '@ahmdigital/logic/lib/content/utils/generate-portable-text';
import React from 'react';
import styled from 'styled-components';

import { TARGET_SEGMENT } from '../../../cms/constants';
import filterOffers from '../utils/filterOffers';
import getContentForOffer from '../tile/utils/get-content-for-offer';
import hasEndPunctuation from '../utils/has-end-punctuation';
import logging from '../../../logging';
import OfferBannerHeader from './components/offer-header';
import offerTagImage from '../images/offer-tag.svg';
import selectReferralCode from '../../../selectors/select-referral-code';
import useGetActivePromoOffer from '../logic/use-get-active-promo-offer';
import type { Offer, OfferPresaleText, OfferRewards, Placeholder } from '../types';

const OfferBannerSection = styled(Section)`
  background: ${({ theme }) => theme.colors.purpleTint};
  padding: 1rem;

  ${({ isInHeroBanner, theme }) =>
    isInHeroBanner &&
    `
      border-bottom: 1px solid ${theme.colors.purple};

      @media (min-width: ${theme.media.sm}) {
        border-left: 0.5rem solid ${theme.colors.black};
        border-right: 0.5rem solid ${theme.colors.black};
        margin-left: auto;
        margin-right: auto;
        max-width: ${theme.grid.wrapperWidthExtraExtraLarge};
      }
  `}
`;

const CodeCopyInline = styled(CodeCopy)`
  display: inline-block;
`;

const StyledCol = styled(Col)`
  @media (max-width: ${({ theme }) => theme.media.sm}) {
    display: none;
  }
`;

const StyledSpan = styled.span`
  display: inline-block;
`;

type OfferBannerProps = {
  filterProductId?: string;
  overrideOffers?: Offer[];
  placeholders?: Placeholder[];
  targetSegment?: string;
  presaleSentence?: OfferPresaleText;
  presaleEnd?: OfferPresaleText;
  rewards?: OfferRewards;
  isInHeroBanner?: boolean;
};

const OfferBanner = ({
  filterProductId,
  overrideOffers,
  placeholders,
  targetSegment = TARGET_SEGMENT.LOOKING_FOR_PRIVATE_HEALTH_INSURANCE,
  presaleSentence,
  presaleEnd,
  rewards,
  isInHeroBanner,
}: OfferBannerProps) => {
  const { activeOffers } = useGetActivePromoOffer({ skip: !isEmpty(overrideOffers), targetSegment });
  const referralCode: string | null = useSelector(selectReferralCode);

  if (isEmpty(activeOffers) && isEmpty(overrideOffers)) {
    return null;
  }

  const getOffersToDisplay = () => {
    const offers = overrideOffers || activeOffers;
    return filterProductId ? filterOffers({ filterProductId, offers }) : offers;
  };

  const offer = (last(getOffersToDisplay()) as Offer) || [];
  if (isEmpty(offer)) {
    logging
      .getLogger()
      .error(
        `OfferBanner: No eligible offer found for ${filterProductId}, issue could be in Sanity as offer count is ${activeOffers?.length}`,
      );

    return null;
  }

  const { ends, header, promoCode, subtext } = getContentForOffer(offer, referralCode);

  const headerText = generatePortableText(header)?.children?.[0]?.text;
  // adding whitespace between sentences
  const endOfSentence = hasEndPunctuation(headerText) ? ' ' : '. ';

  const allOfferCopy = {
    // @ts-expect-error - Automatic, Please fix when editing this file
    value: [...(subtext?.value ?? {}), { value: ' ' }, ...(ends?.value ?? {})],
  };
  const offerText =
    subtext && ends ? (
      <PortableText source={generatePortableText(allOfferCopy)} />
    ) : (
      <PortableText source={generatePortableText(subtext || ends)} />
    );
  const presaleTextSummary =
    presaleSentence && presaleEnd
      ? {
          value: [...(presaleSentence?.value ?? {}), { value: ' ' }, ...(presaleEnd?.value ?? {})],
        }
      : null;

  return (
    <OfferBannerSection isInHeroBanner={isInHeroBanner}>
      <Row>
        <StyledCol df={1}>
          <Image src={offerTagImage.src} alt="Offer" />
        </StyledCol>
        <Col xs={12} df={11}>
          <H2 looksLike="h5">
            {/* @ts-expect-error - Automatic, Please fix when editing this file */}
            <OfferBannerHeader header={header} offer={offer} placeholders={placeholders} rewards={rewards} />
            {endOfSentence}
            <StyledSpan>Use {promoCode !== referralCode && 'our'} code:</StyledSpan>{' '}
            <CodeCopyInline asTextLink codeValue={promoCode} />
          </H2>
          {presaleTextSummary ? <PortableText source={generatePortableText(presaleTextSummary)} /> : offerText}
        </Col>
      </Row>
    </OfferBannerSection>
  );
};
export default OfferBanner;
